import {
  DueDisplayOptions,
  Task,
  Timestamp,
  TranslateFunction,
  WeekDays,
} from '@griply/types';
import {
  differenceInDays,
  endOfDay,
  format,
  isAfter,
  isBefore,
  isSameDay,
} from 'date-fns';

import { getNextOccurrence } from './get-next-occurrence';
import { getActiveSchedule, getScheduleDueDate } from './schedules';
import { shouldDueBeShown } from './should-due-be-shown';

export type GetTaskDueDateOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
};

export const getTaskDueDate = (
  task: Task,
  { today, weekStartsOn }: GetTaskDueDateOptions,
) => {
  const activeSchedule = getActiveSchedule(task.schedules, today);
  if (!activeSchedule) {
    return task.endStrategy?.deadline ?? undefined;
  }

  const scheduleDueDate = getScheduleDueDate(activeSchedule, {
    today,
    weekStartsOn,
  });

  // overdue / today
  if (scheduleDueDate && !isAfter(scheduleDueDate, endOfDay(today))) {
    return scheduleDueDate;
  }

  // The active schedule cannot see a replacement that starts after it ends.
  return getNextOccurrence(task, { today, weekStartsOn })?.date;
};

export type GetDueLabelOptions = {
  t: TranslateFunction;
  today: Timestamp;
  displayDue?: DueDisplayOptions[];
};

export const getDueLabel = (
  date: Timestamp | undefined,
  { t, today, displayDue }: GetDueLabelOptions,
) => {
  if (!date || !shouldDueBeShown(date, displayDue, today)) {
    return;
  }

  if (isSameDay(date, today)) {
    return t('general.dates.today');
  }

  if (isBefore(date, today)) {
    return t('general.days-ago', {
      count: differenceInDays(endOfDay(today), endOfDay(date)),
    });
  }

  return format(date, 'd MMM');
};
